import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import fundo from "../../../../styles/fundo.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";


import logo1 from "./logo-1.png";
import logo6 from "./logo-6.png";


// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;


const useBoletim = (tipo, urlApi, rota = "atualiza-industria", periodo = null) => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {

        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);
        const ordem = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];

        // 🔒 Verifica se é array antes de tentar ordenar
        if (Array.isArray(res.data)) {
          const dadosOrdenados = res.data.sort((a, b) => {
            return ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO);
          });
          setDados(dadosOrdenados);
        } else {
          console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
          setDados([]); // ou setDados([res.data]) se for um único objeto válido
        }
      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };

    fetchDados();
  }, [tipo, urlApi, rota, periodo]);

  return dados;
};

const useBoletim2 = (tipo, urlApi, rota = "atualiza-cct", periodo = null) => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);

        const ordem = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];

        // 🔒 Verifica se é array antes de tentar ordenar
        if (Array.isArray(res.data)) {
          const dadosOrdenados = res.data.sort((a, b) => {
            return ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO);
          });
          setDados(dadosOrdenados);
        } else {
          console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
          setDados([]); // ou setDados([res.data]) se for um único objeto válido
        }

      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };
    fetchDados();
  }, [tipo, urlApi, rota, periodo]);

  return dados;
};

const useHoraRodou = (urlApi, rota = "atualiza-industria") => {
  const [hora, setHora] = useState(null);

  useEffect(() => {
    const fetchHora = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/hora-rodou`);
        setHora(res.data.hora); // já pega a string diretamente
      } catch (error) {
        console.error("Erro ao buscar hora-rodou:", error);
      }
    };



    fetchHora();
  }, [urlApi, rota]);

  return hora;
};

const Grid = ({ urlApi, periodos, zoomIn, zoomOut }) => {
  const horaRodou = useHoraRodou(urlApi);

  const entradaDeCana = useBoletim2("cct-entrada-de-cana", urlApi);
  const moagem = useBoletim2("cct-moagem", urlApi);
  const eficienciaIndustrial = useBoletim("ind-eficiencia-industrial", urlApi);
  const moagemHora = useBoletim("ind-moagem-hora", urlApi);
  const rotacaoMoenda = useBoletim("ind-rotacao-moenda", urlApi);
  const polBagaco = useBoletim("ind-pol-bagaco", urlApi);
  const umidadeBagaco = useBoletim("ind-umidade-bagaco", urlApi);
  const producaoVaporCaldeiraUm = useBoletim("ind-producao-vapor-caldeira-1", urlApi);
  const producaoVaporCaldeiraDois = useBoletim("ind-producao-vapor-caldeira-2", urlApi);
  const polTorta = useBoletim("ind-pol-torta", urlApi);
  const umidadeTortaPrensa = useBoletim("ind-umidade-torta-prensa", urlApi);

  const vazaoCaldoEvaporacao = useBoletim("ind-vazao-caldo-evaporacao", urlApi);
  const brixFinal = useBoletim("ind-brix-final", urlApi);
  const purezaXarope = useBoletim("ind-pureza-xarope", urlApi);
  const purezaMelFinal = useBoletim("ind-pureza-mel-final", urlApi);
  const recuperacaoFabrica = useBoletim("ind-recuperacao-fabrica", urlApi);
  const corAcucar = useBoletim("ind-cor-acucar", urlApi);
  const umidadeAcucar = useBoletim("ind-umidade-acucar", urlApi);
  const viabilidadeCelular = useBoletim("ind-viabilidade-celular", urlApi);
  const concentracaoCentrifuga = useBoletim("ind-concentracao-centrifuga", urlApi);
  const pressaoV1 = useBoletim("ind-pressao-v1", urlApi);
  const pressaoVaporEscape = useBoletim("ind-pressao-vapor-escape", urlApi);

  const paradasRecebidas = useBoletim("ind-paradas-recebidas", urlApi);
  const paradasIndustriais = useBoletim("ind-paradas-industriais", urlApi);
  const eficienciaTempoIndustrial = useBoletim("ind-eficiencia-tempo-industrial", urlApi);
  const aproveitementoTempoGeral = useBoletim("ind-aproceitamento-tempo-geral", urlApi);


  //=======================JUNTA OS MAP======================================//
  const isMobile = window.innerWidth <= 768;
  const codigosPeriodos = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
  const periodosMobile = ['d1', 'd', 'semana', 'mes', 'safra'];

  const LinhaTabela = ({ titulo, dados2 }) => {
    const periodosEmNegrito = ['d', 'semana', 'mes', 'safra'];

    const dadosFiltrados = isMobile
      ? dados2.filter(item => periodosMobile.includes(item.PERIODO))
      : dados2;

    return (
      <Tr>
        <Td><strong>{titulo}</strong></Td>
        {dadosFiltrados.map((item, index) => {
          const valorFormatado = item.VALOR != null && item.VALOR !== 0
            ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "0";

          const estiloFonte = periodosEmNegrito.includes(item.PERIODO)
            ? { fontWeight: 'bold' }
            : {};

          return (
            <Td key={`2-${index}`} alignCenter zebra={index % 2 === 0} style={estiloFonte}>
              {valorFormatado}
            </Td>
          );
        })}
      </Tr>
    );
  };

  //=======================GERAR PDF==========================================//

  const getBase64FromImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = imgSrc;
    });
  };






  //===========================EXPORTAR PDF==============================//
  const gerarPdfRelatorio = async () => {
    const isMobile = window.innerWidth <= 768;
    const periodosOrdenados = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosMobile = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosFiltrados = isMobile ? periodosMobile : periodosOrdenados;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

    // Marca d'água (logo1 sempre)
    const { base64: marcaDaguaBase64, width: mw, height: mh } = await getBase64FromImage(logo1);
    const maxMarcaW = 400;
    const maxMarcaH = 300;
    let marcaW = mw, marcaH = mh;
    if (mw > maxMarcaW || mh > maxMarcaH) {
      const ratio = Math.min(maxMarcaW / mw, maxMarcaH / mh);
      marcaW = mw * ratio;
      marcaH = mh * ratio;
    }
    const centerX = (842 - marcaW) / 2;
    const centerY = (595 - marcaH) / 2;

    // Cabeçalho fixo
    const { base64, width, height } = await getBase64FromImage(logo6);
    let displayW = width, displayH = height;
    const maxW = 200, maxH = 60;
    if (width > maxW || height > maxH) {
      const ratio = Math.min(maxW / width, maxH / height);
      displayW = width * ratio;
      displayH = height * ratio;
    }

    doc.addImage(base64, 'PNG', 20, 10, displayW, displayH);
    doc.setFontSize(12);
    doc.setTextColor(87, 87, 87);
    doc.text("Boletim Cana", 30, 70);
    doc.text(`Última atualização: ${horaRodou}`, 600, 70);

    // Mapeia os códigos d7, d6... para as datas reais
    const mapaCodigosParaData = {};
    periodosOrdenados.forEach((codigo, index) => {
      if (periodos[index]) {
        mapaCodigosParaData[codigo] = periodos[index].DATA;
      }
    });
    mapaCodigosParaData['semana'] = 'Semana';
    mapaCodigosParaData['mes'] = 'Mês';
    mapaCodigosParaData['safra'] = 'Safra';


    // Cabeçalhos
    const headers = [
      "Período",
      ...periodosFiltrados.map(p => mapaCodigosParaData[p] || p.toUpperCase())
    ];



    // Corpo
    const body = [];

    const montarLinha = (titulo, dados) => {
      const linha = [titulo];
      periodosFiltrados.forEach(periodo => {
        const item = dados.find(d => d.PERIODO === periodo);
        linha.push(item && item.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "0");
      });
      body.push(linha);
    };

    // Linhas da tabela
    montarLinha("Entrada de Cana (ton)", entradaDeCana);
    montarLinha("Moagem (ton)", moagem);
    montarLinha("Eficiência Indústrial Copersucar (%)", eficienciaIndustrial);
    montarLinha("Moagem Hora (ton/hora)", moagemHora);
    montarLinha("Rotação Moenda (rpm)", rotacaoMoenda);
    montarLinha("Pol Bagaço (%)", polBagaco);
    montarLinha("Umidade Bagaço (%)", umidadeBagaco);
    montarLinha("Produção Vapor Caldeira 1 (ton)", producaoVaporCaldeiraUm);
    montarLinha("Produção Vapor Caldeira 2 (ton)", producaoVaporCaldeiraDois);
    montarLinha("Pol Torta (%)", polTorta);
    montarLinha("Umidade Torta - Prensa (%)", umidadeTortaPrensa);

    montarLinha("Vazão Caldo Evaporação (m³)", vazaoCaldoEvaporacao);
    montarLinha("Brix Final (%)", brixFinal);
    montarLinha("Pureza Xarope (%)", purezaXarope);
    montarLinha("Pureza Mel Final (%)", purezaMelFinal);
    montarLinha("Recuperação da Fábrica (sjm)", recuperacaoFabrica);
    montarLinha("Cor do Açúcar (ui)", corAcucar);
    montarLinha("Umidade do Açúcar (%)", umidadeAcucar);
    montarLinha("Viabilidade Celular (%)", viabilidadeCelular);
    montarLinha("Concentração das Centrifugas (%)", concentracaoCentrifuga);
    montarLinha("Pressão V1 - Destilaria (kgf/cm²)", pressaoV1);
    montarLinha("Pressão Vapor Escape (kgf/cm²)", pressaoVaporEscape);
    montarLinha("Paradas Recebimento (h)", paradasRecebidas);
    montarLinha("Paradas Indústriais (h)", paradasIndustriais);
    montarLinha("Eficiência de Tempo Industrial (%)", eficienciaTempoIndustrial);
    montarLinha("Aproveitamento de Tempo Geral (%)", aproveitementoTempoGeral);

    // Tabela com autoTable
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 80,
      margin: { top: 50, left: 20, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
      didDrawPage: () => {
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.addImage(marcaDaguaBase64, 'JPEG', centerX, centerY, marcaW, marcaH);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    });

    doc.save(`boletim_cana_${new Date().toLocaleString("pt-BR")}.pdf`);
  };



  //===========================TELA CHEIA==============================//
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable full - screen mode: ${err.message}(${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };






  return (
    <>

      <Geral>
        <div>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Cana</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                      <IconGroup>
                        <Button onClick={gerarPdfRelatorio} placeholder="Imprimir">
                          <FaDownload />
                        </Button>
                        <Button
                          onClick={zoomOut}
                          title="Diminuir zoom (Alt+-)"
                          aria-label="Diminuir zoom"
                          type="button"
                        >
                          <FiZoomOut />
                        </Button>
                        <Button
                          onClick={zoomIn}
                          title="Aumentar zoom (Alt+=)"
                          aria-label="Aumentar zoom"
                          type="button"
                        >
                          <FiZoomIn />
                        </Button>
                      </IconGroup>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>

              <LinhaTabela titulo="Entrada de Cana (ton)" dados2={entradaDeCana} />
              <LinhaTabela titulo="Moagem (ton)" dados2={moagem} />
              <LinhaTabela titulo="Eficiência Indústrial Copersucar (%)" dados2={eficienciaIndustrial} />
              <LinhaTabela titulo="Moagem Hora (ton/hora)" dados2={moagemHora} />
              <LinhaTabela titulo="Rotação Moenda (rpm)" dados2={rotacaoMoenda} />
              <LinhaTabela titulo="Pol Bagaço (%)" dados2={polBagaco} />
              <LinhaTabela titulo="Umidade Bagaço (%)" dados2={umidadeBagaco} />
              <LinhaTabela titulo="Produção Vapor Caldeira 1 (ton)" dados2={producaoVaporCaldeiraUm} />
              <LinhaTabela titulo="Produção Vapor Caldeira 2 (ton)" dados2={producaoVaporCaldeiraDois} />
              <LinhaTabela titulo="Pol Torta (%)" dados2={polTorta} />
              <LinhaTabela titulo="Umidade Torta - Prensa (%)" dados2={umidadeTortaPrensa} />
              <LinhaTabela titulo="Vazão Caldo Evaporação (m³)" dados2={vazaoCaldoEvaporacao} />
              <LinhaTabela titulo="Brix Final (%)" dados2={brixFinal} />
              <LinhaTabela titulo="Pureza Xarope (%)" dados2={purezaXarope} />
              <LinhaTabela titulo="Pureza Mel Final (%)" dados2={purezaMelFinal} />
              <LinhaTabela titulo="Recuperação da Fábrica (sjm)" dados2={recuperacaoFabrica} />
              <LinhaTabela titulo="Cor do Açúcar (ui)" dados2={corAcucar} />
              <LinhaTabela titulo="Umidade do Açúcar (%)" dados2={umidadeAcucar} />
              <LinhaTabela titulo="Viabilidade Celular (%)" dados2={viabilidadeCelular} />
              <LinhaTabela titulo="Concentração das Centrifugas (%)" dados2={concentracaoCentrifuga} />
              <LinhaTabela titulo="Pressão V1 - Destilaria (kgf/cm²)" dados2={pressaoV1} />
              <LinhaTabela titulo="Pressão Vapor Escape (kgf/cm²)" dados2={pressaoVaporEscape} />
              <LinhaTabela titulo="Paradas Recebimento (h)" dados2={paradasRecebidas} />
              <LinhaTabela titulo="Paradas Indústriais (h)" dados2={paradasIndustriais} />
              <LinhaTabela titulo="Eficiência de Tempo Industrial (%)" dados2={eficienciaTempoIndustrial} />
              <LinhaTabela titulo="Aproveitamento de Tempo Geral (%)" dados2={aproveitementoTempoGeral} />

            </Tbody>
          </Table>


        </div>

      </Geral >

    </>

  );
};



export default Grid;
